import type { IHelpdeskConfig, IHelpdeskTicketContext } from "@kuika/kuika-cl-model";
import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { withContext, IWithContextProp } from "../../../shared/hoc/with-context";
import { withManagement } from "../../../shared/hoc/with-management";
import KuikaSpinner from "../../ui-kit/kuika-spinner/kuika-spinner";
import KuikaTextMedium from "../../ui-kit/typography/TextMedium/TextMedium";
import { useHelpdeskConfig } from "./helpdesk-config";
import {
  clearPendingHelpdeskTicket,
  helpdeskPath,
  HELPDESK_TICKET_PARAM,
  parseTicketNumber,
  readPendingHelpdeskTicket
} from "./helpdesk-deep-link";
import ItsmHelpdesk from "./itsm-helpdesk";

interface IHelpdeskPageParams {
  domainName?: string;
}

// Outside a project there is no app or screen to record; the server adds the workspace.
const NO_TICKET_CONTEXT = (): IHelpdeskTicketContext => ({});
const NO_BADGE = (): void => {};

/** The ticket the page opens on: the address first, then a link remembered across the login page. */
function takeInitialTicket(): number | null {
  const fromAddress: number | null = parseTicketNumber(
    new URLSearchParams(window.location.search).get(HELPDESK_TICKET_PARAM)
  );
  const ticketNumber: number | null = fromAddress ?? readPendingHelpdeskTicket();
  clearPendingHelpdeskTicket();
  return ticketNumber;
}

/**
 * `/{workspace}/helpdesk?ticket={n}`: the full-page Kuika ITSM helpdesk the "View ticket" link of
 * an ITSM mail opens. Not offered for the Zendesk helpdesk, which keeps its own screens.
 */
const HelpdeskPageInner = ({ context }: IWithContextProp): React.ReactElement => {
  const config: IHelpdeskConfig | null = useHelpdeskConfig();
  const navigate = useNavigate();
  const { domainName } = useParams<keyof IHelpdeskPageParams>();
  const [initialTicketNumber] = useState<number | null>(takeInitialTicket);

  useEffect((): void => {
    // The ticket is open now; a reload should not keep jumping back to it.
    if (window.location.search) navigate(helpdeskPath(domainName), { replace: true });
  }, [domainName, navigate]);

  if (!config) {
    return (
      <div className="kuika-helpdesk-page__state">
        <KuikaSpinner />
      </div>
    );
  }

  if (config.provider !== "Itsm") return <Navigate to="/" replace />;

  return (
    <div className="kuika-helpdesk-page" data-testid="helpdeskPage">
      <header className="kuika-helpdesk-page__header">
        <KuikaTextMedium>Help desk</KuikaTextMedium>
      </header>
      <div className="kuika-helpdesk-page__body">
        <ItsmHelpdesk
          userId={context?.userInfo?.id}
          workspaceName={domainName ?? context?.userInfo?.lastUsedDomainName}
          limits={config.limits}
          initialTicketNumber={initialTicketNumber}
          getTicketContext={NO_TICKET_CONTEXT}
          onUnreadChanged={NO_BADGE}
        />
      </div>
    </div>
  );
};

const HelpdeskPage = withManagement<object>(withContext<object>(HelpdeskPageInner));

/** `/helpdesk?ticket={n}`, the address the ITSM's mails carry: moves to the user's workspace. */
const HelpdeskEntryRedirect = withContext<object>(({ context }: IWithContextProp): React.ReactElement => {
  const ticketNumber: number | null = parseTicketNumber(
    new URLSearchParams(window.location.search).get(HELPDESK_TICKET_PARAM)
  );
  const domainName: string | undefined = context?.userInfo?.lastUsedDomainName;
  if (!domainName) {
    // No workspace to open it in: drop the link rather than bounce between the two routes.
    clearPendingHelpdeskTicket();
    return <Navigate to="/" replace />;
  }
  return <Navigate to={helpdeskPath(domainName, ticketNumber)} replace />;
});

export { HelpdeskEntryRedirect, HelpdeskPage, HelpdeskPageInner };
